/**
 * Wall-clock -> instant resolution across DST boundaries (TASK-674 review).
 *
 * `new Date('YYYY-MM-DDTHH:mm')` reads the entry in the browser's zone, which is the right
 * contract — the RM types the time the meeting happens. But twice a year that mapping is not
 * a function:
 *
 *   * SPRING GAP — the clocks jump forward, so an hour of wall-clock time does not exist.
 *     Zurich 2026-03-29 02:30 is not a moment. JS silently rolls it forward to 03:30 and
 *     returns a Date, so the RM books a meeting at a time they did not choose and is never
 *     told.
 *   * AUTUMN OVERLAP — the clocks go back, so an hour happens twice. Zurich 2026-10-25 02:30
 *     is TWO instants (00:30Z in CEST and 01:30Z in CET). JS picks one. A 50% chance of
 *     being an hour wrong, silently.
 *
 * Both are rare and both are exactly the kind of "silently shifted the appointment" the
 * ticket forbids. This resolver reports them instead of guessing; the caller must present
 * the valid choices and let the RM pick.
 *
 * Deliberately zone-agnostic: it works off the runtime's own offsets rather than a tz
 * database, so it is correct for whatever zone the RM's browser is in, with no dependency.
 */

export type LocalInstant =
  | { kind: 'ok'; iso: string }
  /** The entered wall clock does not exist. `options` are the instants either side of the gap. */
  | { kind: 'gap'; options: InstantChoice[] }
  /** The entered wall clock happens twice. `options` are both occurrences, in order. */
  | { kind: 'ambiguous'; options: InstantChoice[] }
  | { kind: 'invalid' };

export interface InstantChoice {
  /** The instant, as the UTC ISO string the API stores. */
  iso: string;
  /** How that instant reads on the RM's clock, e.g. "03:30 GMT+2". */
  label: string;
}

const HOUR_MS = 3_600_000;

function labelFor(d: Date): string {
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName')?.value;
  return zone ? `${time} ${zone}` : time;
}

const choice = (d: Date): InstantChoice => ({ iso: d.toISOString(), label: labelFor(d) });

/**
 * @param date `YYYY-MM-DD` as typed in the date input
 * @param time `HH:mm` as typed in the time input
 */
export function resolveLocalInstant(date: string, time: string): LocalInstant {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => !Number.isFinite(n))) return { kind: 'invalid' };

  // Month is 0-based here; this constructor interprets the fields in the LOCAL zone, which
  // is the whole point — and is also what normalises a gap time forward.
  const guess = new Date(y, m - 1, d, hh, mm, 0, 0);
  if (Number.isNaN(guess.getTime())) return { kind: 'invalid' };

  // GAP: the runtime could not represent what was typed, so it moved it.
  if (guess.getHours() !== hh || guess.getMinutes() !== mm) {
    // The two real instants bracketing the gap: the same wall clock an hour earlier
    // (before the jump) and the rolled-forward value the runtime produced (after it).
    const before = new Date(guess.getTime() - HOUR_MS);
    return { kind: 'gap', options: [choice(before), choice(guess)] };
  }

  // OVERLAP: if the instant an hour later renders the SAME wall clock, the clock repeated.
  const later = new Date(guess.getTime() + HOUR_MS);
  if (later.getHours() === hh && later.getMinutes() === mm) {
    return { kind: 'ambiguous', options: [choice(guess), choice(later)] };
  }

  return { kind: 'ok', iso: guess.toISOString() };
}
